import React, { FC, useState } from 'react';
import { Box, Accordion, AccordionSummary, AccordionDetails, Typography, Button, Grid, Badge } from '@mui/material';
import { CampaignCoupons, CampaignModal, CouponModel, Customer, JimboResponse } from '@models';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SubTitle } from '@components';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import { addErrorToLog } from '@store/slices/error-log';
import { useGetCampaignsQuery, useCreateCouponsMutation, useGetCustomerCouponsQuery } from '@services';
import CreateCouponModal from './CreateCoupons/CreateCouponModal';
import DeleteCouponModal from './DeleteCoupons/DeleteCouponModal';
import { showToast } from '@store/slices/notification';
import VoucherCard from './VoucherCard';
import moment from 'moment';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import UsedCouponView from './UsedCouponsView';
import { useCustomer } from '@hooks/useCustomer';

type Props = {
  selectedCustomer: Customer;
};

export const CouponsView: FC<Props> = ({ selectedCustomer }) => {
  const [showCreateCoupon, setShowCreateCoupon] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState<CampaignCoupons | null>(null);
  const [openDeleteCouponModal, setOpenDeleteCouponModal] = useState(false);
  const dispatch = useAppDispatch();
  const { getAttribute } = useCustomer();

  const activeUser = useAppSelector((state) => state.activeUser.value);

  const userAccessStatus = activeUser?.user?.roles.find((role: any) => role.moduleName === 'customer');

  const { data: allCampaigns } = useGetCampaignsQuery();
  const { data: customerCoupons } = useGetCustomerCouponsQuery({ customerId: selectedCustomer.id.toString() });
  const [createCoupon] = useCreateCouponsMutation();

  const userHasCoupons = customerCoupons?.coupons;

  // Get todays date
  const todaysDate = moment().toDate();

  // filter for active coupons
  const activeCoupons = customerCoupons?.coupons?.filter(
    (coupon) =>
      coupon.usageCounter < coupon.usageLimit &&
      coupon.state === 'active' &&
      (new Date(coupon.expiryDate) > todaysDate || !coupon.expiryDate),
  );

  // filter for used coupons
  const usedCoupons =
    customerCoupons?.coupons?.filter((coupon) => coupon.usageCounter >= coupon.usageLimit && coupon.state === 'used') || [];

  // filter for expired coupons
  const expiredCoupons = customerCoupons?.coupons?.filter(
    (coupon) => coupon.state === 'expired' || new Date(coupon.expiryDate) < todaysDate,
  );

  // is customer have ID coupons
  const isIDVerifiedCoupons = customerCoupons?.coupons?.filter(
    (coupon) => coupon.campaignId === Number(process.env.REACT_APP_T1_IDVERIFICATION_CAMP_ID),
  );

  const isIDVerified = !!getAttribute('IDVerifiedOn');
  const isCustomerIDEligable = isIDVerified && !isIDVerifiedCoupons?.length;

  const onCreateCoupons = (data: CouponModel) => {
    const couponModal: CouponModel = {
      ...data,
      customerId: selectedCustomer.id.toString(),
    };

    createCoupon(couponModal)
      .unwrap()
      .then((data) => {
        dispatch(
          showToast({
            message: `Successfully updated coupon for the customer`,
            type: 'success',
          }),
        );

        setShowCreateCoupon(false);
      })
      .catch((error: { data: JimboResponse }) => {
        dispatch(addErrorToLog(error?.data));

        dispatch(
          showToast({
            message: `Failed to create coupon!`,
            type: 'error',
          }),
        );

        setShowCreateCoupon(false);
      });
  };

  const getCampaign = (campaignId: number) => {
    const campName = allCampaigns?.data.find((campaign: CampaignModal) => campaign.id === campaignId);
    return campName?.name ?? 'Unkown campaign';
  };

  const showHideModal = (couponId: number | undefined, isShow = false) => {
    usedCoupons?.forEach((coupon) => (coupon.id === couponId ? { ...coupon, showModal: isShow } : coupon));
  };

  return (
    <Box>
      <Grid container display="flex" bgcolor="primary.lighter">
        <Grid item width="50%" display="inline-block">
          <SubTitle title="Coupons" />
        </Grid>
        <Grid item sx={{ verticalAlign: 'center', padding: '8px' }} width="50%" justifyContent="flex-end" alignItems="center">
          {userAccessStatus.role > 1 && (
            <Button
              variant="contained"
              sx={{ float: 'right' }}
              data-testid="create-coupon-button"
              onClick={() => setShowCreateCoupon(true)}
            >
              Create Coupon
            </Button>
          )}
        </Grid>
      </Grid>
      <CreateCouponModal
        open={showCreateCoupon}
        handleClose={() => setShowCreateCoupon(false)}
        isCustomerIDEligable={isCustomerIDEligable}
        allCampaigns={allCampaigns?.data ?? []}
        onSave={onCreateCoupons}
      />
      {userHasCoupons ? (
        <Box>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid="active-coupons-accordion">
              <Typography>Active Coupons</Typography>
            </AccordionSummary>
            <AccordionDetails data-testid="active-coupons">
              <Grid container spacing={2}>
                {activeCoupons?.map((coupon) => (
                  <Grid item xs={12} sm={6} md={3} key={coupon.id}>
                    <Badge badgeContent={coupon.usageLimit} color="primary">
                      <VoucherCard data-testid="voucher-card">
                        <Grid container>
                          <Grid item xs={12}>
                            <Typography variant="subtitle1">{getCampaign(coupon.campaignId ?? 0)}</Typography>
                          </Grid>
                          <Grid item xs={12} marginBottom={1}>
                            <Typography variant="body1"> {coupon.value}</Typography>
                          </Grid>

                          {coupon.discountRemainder && (
                            <Grid item xs={12} justifyContent="center">
                              <Typography variant="body1">Remaining value : ${coupon.discountRemainder}</Typography>
                            </Grid>
                          )}
                          {coupon.created && (
                            <Grid item xs={12}>
                              <Typography variant="caption">
                                Created on{' '}
                                {new Date(coupon.created).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </Typography>
                            </Grid>
                          )}
                          {coupon.expiryDate && (
                            <Grid item xs={12}>
                              <Typography variant="caption">
                                Expiry on{' '}
                                {new Date(coupon.expiryDate).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </Typography>
                            </Grid>
                          )}
                          {userAccessStatus.role > 1 && (
                            <Grid item xs={12}>
                              <Box sx={{ position: 'absolute', left: '225px', bottom: '5px' }}>
                                <IconButton
                                  aria-label="delete"
                                  data-testid="delete-coupon-modal-button"
                                  size="small"
                                  onClick={() => {
                                    setOpenDeleteCouponModal(true);
                                    setCouponToDelete(coupon as unknown as CampaignCoupons);
                                    return false;
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </VoucherCard>
                    </Badge>
                  </Grid>
                ))}
                <DeleteCouponModal
                  isOpen={openDeleteCouponModal}
                  couponToDelete={couponToDelete}
                  handleClose={() => setOpenDeleteCouponModal(false)}
                  setModal={setOpenDeleteCouponModal}
                />
              </Grid>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid="used-coupons-accordion">
              <Typography>Used Coupons</Typography>
            </AccordionSummary>
            <AccordionDetails data-testid="used-coupons">
              <Grid container spacing={2}>
                {usedCoupons?.map((coupon) => (
                  <UsedCouponView coupon={coupon} key={coupon.id} campaignName={getCampaign(coupon.campaignId ?? 0)} />
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid="expired-coupons-accordion">
              <Typography>Expired Coupons</Typography>
            </AccordionSummary>
            <AccordionDetails data-testid="expired-coupons">
              <Grid container spacing={2}>
                {expiredCoupons?.map((coupon) => (
                  <Grid item xs={12} sm={6} md={3} key={coupon.id}>
                    <Badge badgeContent={coupon.usageLimit} color="primary">
                      <VoucherCard data-testid="voucher-card">
                        <Grid container>
                          <Grid item xs={12}>
                            <Typography variant="subtitle1">{getCampaign(coupon.campaignId ?? 0)}</Typography>
                          </Grid>
                          <Grid item xs={12} marginBottom={1}>
                            <Typography variant="body1"> {coupon.value}</Typography>
                          </Grid>

                          {coupon.discountRemainder && (
                            <Grid item xs={12} justifyContent="center">
                              <Typography variant="body1">Remaining value : ${coupon.discountRemainder}</Typography>
                            </Grid>
                          )}
                          {coupon.expiryDate && (
                            <Grid item xs={12}>
                              <Typography variant="caption">
                                Expiry on{' '}
                                {new Date(coupon.expiryDate).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </VoucherCard>
                    </Badge>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Box>
      ) : (
        <Box>
          <Typography data-testid="no-coupons-text">No coupons found</Typography>
        </Box>
      )}
    </Box>
  );
};
